import React, { useEffect, useMemo, useState } from "react";

type StudentInfo = {
  id: number | null;
  fullName: string;
  email: string;
  phone: string;
  studentClass: string;
  course: string;
  profileImage: string;
};

type Course = {
  id: number;
  title: string;
  category?: string;
  teacher?: string;
  price?: number;
  image_url?: string;
  status?: string;
  progress?: number;
  subjectsCount?: number;
  liveCount?: number;
  recordingCount?: number;
  testCount?: number;
};

// const PROFILE_API = "http://localhost:9090/api/profile";
// const COURSES_API = "http://localhost:9090/api/courses";

const PROFILE_API = "https://node-api.mediitacademy.com/api/profile";
const COURSES_API = "https://node-api.mediitacademy.com/api/courses";

const MyCourses: React.FC = () => {
  const [student, setStudent] = useState<StudentInfo>({
    id: null,
    fullName: "",
    email: "",
    phone: "",
    studentClass: "",
    course: "",
    profileImage: "",
  });

  const [allCourses, setAllCourses] = useState<Course[]>([]);
  const [myCourses, setMyCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const getToken = () => localStorage.getItem("token") || "";

  const getStoredStudentId = () => {
    const directId =
      localStorage.getItem("studentId") ||
      localStorage.getItem("userId") ||
      localStorage.getItem("id");

    if (directId) return Number(directId);

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return Number(user?.id || user?.studentId || 1);
    } catch {
      return 1;
    }
  };

  const normalizeCourse = (item: any): Course => {
    return {
      id: Number(item.id || item.courseId || 0),
      title: item.title || item.name || item.courseName || "Course",
      category: item.category || item.studentClass || item.className || "Course",
      teacher: item.teacher || item.teacherName || item.mentor || "Assigned Teacher",
      price: Number(item.price || item.amount || 0),
      image_url: item.image_url || item.imageUrl || item.thumbnail || "",
      status: item.status || "ACTIVE",
      progress: Number(item.progress || item.completedPercentage || 0),
      subjectsCount: Number(item.subjectsCount || item.totalSubjects || 0),
      liveCount: Number(item.liveCount || item.totalLiveClasses || 0),
      recordingCount: Number(item.recordingCount || item.totalRecordings || 0),
      testCount: Number(item.testCount || item.totalTests || 0),
    };
  };

  const fetchProfile = async () => {
    const token = getToken();

    if (!token) {
      window.location.href = "/login";
      return null;
    }

    const response = await fetch(PROFILE_API, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.error || "Profile loading failed");
    }

    const nextStudent: StudentInfo = {
      id: Number(data.id || data.user_id || getStoredStudentId()),
      fullName: data.full_name || data.name || "Student",
      email: data.email || "",
      phone: data.mobile_number || data.phone || "",
      studentClass: data.school || data.studentClass || data.class || "",
      course: data.course || "",
      profileImage: data.profile_image
        ? `https://node-api.mediitacademy.com${data.profile_image}`
        : "",
    };

    setStudent(nextStudent);
    localStorage.setItem("studentId", String(nextStudent.id || 1));

    return nextStudent;
  };

  const fetchAllCourses = async () => {
    const response = await fetch(COURSES_API);

    if (!response.ok) {
      throw new Error("Courses loading failed");
    }

    const data = await response.json();
    const normalized = Array.isArray(data) ? data.map(normalizeCourse) : [];

    setAllCourses(normalized);
    return normalized;
  };

  // ✅ FIX 3: Port updated to 9091
  const fetchMyCourses = async (email: string) => {
    if (!email) return [];
    try {
      const response = await fetch(`http://localhost:9091/api/enrollments/by-email/${encodeURIComponent(email)}`);
      if (!response.ok) return [];
      
      const data = await response.json();
      const enrollments = Array.isArray(data) ? data : [];

      return enrollments.filter((e: any) => {
          const status = String(e.status || e.paymentStatus || "").toUpperCase();
          return e.courseAccessEnabled || status === "APPROVED" || status === "PAID" || status === "SUCCESS";
      });
    } catch (error) {
      return [];
    }
  };

  const loadData = async () => {
    setLoading(true);

    try {
      const profile = await fetchProfile();
      if (!profile) return;

      const allFetchedCourses = await fetchAllCourses();
      const activeEnrollments = await fetchMyCourses(profile.email || "");

      if (activeEnrollments.length > 0) {
        const mappedCourses = activeEnrollments.map((e: any) => {
          const matched = allFetchedCourses.find(c => c.id === e.courseId);
          
          // ✅ FIX 4: Used optional chaining (?.) to solve TypeScript error
          return {
            ...(matched || {}),
            id: e.courseId || matched?.id || Math.random(),
            title: e.courseTitle || matched?.title || "Enrolled Course",
            status: "ACTIVE",
            progress: 0,
          } as Course;
        });
        setMyCourses(mappedCourses);
        return;
      }

      if (profile?.course) {
        const matchedCourse = allFetchedCourses.find(
          (course) => course.title.toLowerCase().trim() === profile.course.toLowerCase().trim()
        );
        if (matchedCourse) setMyCourses([matchedCourse]);
      }
    } catch (error) {
      console.error("My courses loading error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredMyCourses = useMemo(() => {
    const value = search.toLowerCase().trim();

    if (!value) return myCourses;

    return myCourses.filter((course) =>
      `${course.title} ${course.category} ${course.teacher}`
        .toLowerCase()
        .includes(value)
    );
  }, [myCourses, search]);

  const recommendedCourses = useMemo(() => {
    const activeTitles = myCourses.map((course) =>
      course.title.toLowerCase().trim()
    );

    return allCourses
      .filter(
        (course) => !activeTitles.includes(course.title.toLowerCase().trim())
      )
      .slice(0, 4);
  }, [allCourses, myCourses]);

  const totalProgress =
    myCourses.length > 0
      ? Math.round(
          myCourses.reduce((sum, item) => sum + Number(item.progress || 0), 0) /
            myCourses.length
        )
      : 0;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#edf4ff]">
        <div className="rounded-[30px] bg-white px-8 py-7 text-center shadow-sm">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600" />
          <p className="mt-4 text-sm font-black text-slate-600">
            Loading your courses...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#edf4ff] text-[#101827] font-['Inter']">
      <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/95 px-4 py-3 shadow-sm backdrop-blur-xl sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => (window.location.href = "/student-dashboard")}
              className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600 text-xl font-black text-white"
            >
              M
            </button>

            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.22em] text-blue-700">
                Student Courses
              </p>
              <h1 className="text-xl font-black text-slate-950 sm:text-2xl">
                My Courses
              </h1>
            </div>
          </div>

          <div className="hidden max-w-xl flex-1 md:block">
            <div className="flex h-12 items-center gap-3 rounded-[18px] border border-slate-200 bg-slate-50 px-4">
              <span className="text-slate-400">⌕</span>
              <input
                id="my-course-search"
                name="my-course-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search your courses..."
                className="h-full w-full bg-transparent text-sm font-semibold text-slate-700 outline-none placeholder:text-slate-400"
              />
            </div>
          </div>

          <button
            onClick={() => (window.location.href = "/student-dashboard/profile")}
            className="flex items-center gap-3 rounded-[18px] border border-slate-200 bg-white px-3 py-2 shadow-sm hover:bg-blue-50"
          >
            <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-2xl bg-blue-50 text-sm font-black text-blue-700">
              {student.profileImage ? (
                <img
                  src={student.profileImage}
                  alt={student.fullName || "Student"}
                  className="h-full w-full object-cover"
                />
              ) : (
                (student.fullName || "S").charAt(0).toUpperCase()
              )}
            </span>
            <span className="hidden text-left sm:block">
              <span className="block text-sm font-black text-slate-950">
                {student.fullName || "Student"}
              </span>
              <span className="block text-[11px] font-bold text-slate-500">
                {student.studentClass || "Student"}
              </span>
            </span>
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] space-y-6 p-4 sm:p-6 lg:p-8">
        <section className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
          <div className="relative overflow-hidden rounded-[40px] bg-gradient-to-br from-[#08234a] via-[#123b7a] to-[#2563eb] p-7 text-white shadow-sm sm:p-8">
            <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-20 left-24 h-60 w-60 rounded-full bg-cyan-300/20 blur-3xl" />

            <div className="relative">
              <p className="text-sm font-black uppercase tracking-wide text-blue-100">
                Welcome Back
              </p>
              <h2 className="mt-3 max-w-3xl text-3xl font-black tracking-[-0.04em] sm:text-5xl">
                Continue your learning journey.
              </h2>
              <p className="mt-4 max-w-2xl text-sm font-semibold leading-6 text-blue-100">
                Here, students will see only their enrolled/active courses. Inside each course, live classes, recordings, notes, and tests will be connected.

              </p>

              <div className="mt-7 grid max-w-3xl gap-4 sm:grid-cols-4">
                <StatBox label="My Courses" value={String(myCourses.length)} />
                <StatBox label="Progress" value={`${totalProgress}%`} />
                <StatBox
                  label="Live"
                  value={String(
                    myCourses.reduce((sum, item) => sum + (item.liveCount || 0), 0)
                  )}
                />
                <StatBox
                  label="Tests"
                  value={String(
                    myCourses.reduce((sum, item) => sum + (item.testCount || 0), 0)
                  )}
                />
              </div>
            </div>
          </div>

          <div className="rounded-[38px] bg-white p-6 shadow-sm">
            <p className="text-sm font-black uppercase tracking-wide text-blue-600">
              Student Access
            </p>

            <div className="mt-5 space-y-4">
              <InfoRow label="Name" value={student.fullName || "Student"} />
              <InfoRow label="Class" value={student.studentClass || "--"} />
              <InfoRow
                label="Current Course"
                value={student.course || "Demo Access"}
              />
              <InfoRow
                label="Status"
                value={myCourses.length > 0 ? "Active" : "Demo"}
              />
            </div>

            <button
              onClick={() => (window.location.href = "/student-dashboard")}
              className="mt-6 w-full rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white hover:bg-blue-600"
            >
              Back to Dashboard
            </button>
          </div>
        </section>

        <section className="rounded-[34px] bg-white p-6 shadow-sm">
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div>
              <p className="text-sm font-black uppercase tracking-wide text-blue-600">
                Active Courses
              </p>
              <h2 className="mt-2 text-2xl font-black text-slate-950">
                Your enrolled courses
              </h2>
              <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">
                Student ko yahi se course details, subjects, live classes,
                recordings, notes aur tests open karne ka flow milega.
              </p>
            </div>

            <button
              onClick={() => (window.location.href = "/courses")}
              className="rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white hover:bg-blue-700"
            >
              Explore More Courses
            </button>
          </div>

          <div className="mt-6 grid gap-5 xl:grid-cols-2">
            {filteredMyCourses.map((course) => (
              <CourseCard key={course.id || course.title} course={course} />
            ))}

            {filteredMyCourses.length === 0 && (
              <EmptyState
                title="No active course found"
                desc="Abhi aap demo access par ho. Full course purchase/approval ke baad course yaha show hoga."
              />
            )}
          </div>
        </section>

        {myCourses.length === 0 && recommendedCourses.length > 0 && (
          <section className="rounded-[34px] bg-white p-6 shadow-sm">
            <p className="text-sm font-black uppercase tracking-wide text-blue-600">
              Recommended
            </p>
            <h2 className="mt-2 text-2xl font-black text-slate-950">
              Courses you can subscribe
            </h2>

            <div className="mt-6 grid gap-5 xl:grid-cols-2">
              {recommendedCourses.map((course) => (
                <RecommendedCourseCard key={course.id} course={course} />
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

const CourseCard = ({ course }: { course: Course }) => {
  const progress = Number(course.progress || 0);

  return (
    <div className="overflow-hidden rounded-[32px] border border-slate-100 bg-[#f8fbff] p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
      <div className="flex gap-5">
        <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-[26px] bg-blue-100 text-3xl font-black text-blue-700">
          {course.image_url ? (
            <img
              src={course.image_url}
              alt={course.title}
              className="h-full w-full object-cover"
            />
          ) : (
            "📘"
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-xl font-black text-slate-950">
                {course.title}
              </h3>
              <p className="mt-1 text-sm font-bold text-slate-500">
                {course.category || "Course"} • {course.teacher || "Mentor"}
              </p>
            </div>

            <span className="rounded-full bg-emerald-50 px-4 py-2 text-xs font-black text-emerald-700">
              Active
            </span>
          </div>

          <div className="mt-5">
            <div className="mb-2 flex justify-between text-xs font-black text-slate-500">
              <span>Course Progress</span>
              <span>{progress}%</span>
            </div>
            <div className="h-3 overflow-hidden rounded-full bg-white">
              <div
                className="h-full rounded-full bg-blue-600"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-4">
        <SmallCount label="Subjects" value={course.subjectsCount || 0} />
        <SmallCount label="Live" value={course.liveCount || 0} />
        <SmallCount label="Recordings" value={course.recordingCount || 0} />
        <SmallCount label="Tests" value={course.testCount || 0} />
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-4">
        <ActionButton
          label="Open Course"
          onClick={() =>
            (window.location.href = `/student-dashboard/course/${course.id}`)
          }
        />
        <ActionButton
          label="Live Classes"
          onClick={() =>
            (window.location.href = `/student-dashboard/course/${course.id}?tab=live`)
          }
        />
        <ActionButton
          label="Recordings"
          onClick={() =>
            (window.location.href = `/student-dashboard/course/${course.id}?tab=recordings`)
          }
        />
        <ActionButton
          label="Tests"
          onClick={() =>
            (window.location.href = `/student-dashboard/course/${course.id}?tab=tests`)
          }
        />
      </div>
    </div>
  );
};

const RecommendedCourseCard = ({ course }: { course: Course }) => {
  return (
    <div className="rounded-[30px] border border-slate-100 bg-[#f8fbff] p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-xl font-black text-slate-950">{course.title}</h3>
          <p className="mt-2 text-sm font-bold text-slate-500">
            {course.category || "Course"} • {course.teacher || "Mentor"}
          </p>
        </div>

        <span className="rounded-full bg-blue-50 px-4 py-2 text-xs font-black text-blue-700">
          ₹{course.price || "—"}
        </span>
      </div>

      <button
        onClick={() => (window.location.href = `/course-purchase/${course.id}`)}
        className="mt-6 w-full rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white hover:bg-blue-700"
      >
        Subscribe Now
      </button>
    </div>
  );
};

const StatBox = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="rounded-[24px] border border-white/15 bg-white/10 p-4 backdrop-blur">
      <p className="text-xs font-black uppercase tracking-wide text-blue-100">
        {label}
      </p>
      <p className="mt-2 text-3xl font-black text-white">{value}</p>
    </div>
  );
};

const SmallCount = ({ label, value }: { label: string; value: number }) => {
  return (
    <div className="rounded-2xl bg-white p-4 text-center">
      <p className="text-2xl font-black text-slate-950">{value}</p>
      <p className="mt-1 text-xs font-black text-slate-500">{label}</p>
    </div>
  );
};

const ActionButton = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => {
  return (
    <button
      onClick={onClick}
      className="rounded-2xl bg-white px-4 py-3 text-xs font-black text-slate-700 shadow-sm hover:bg-blue-600 hover:text-white"
    >
      {label}
    </button>
  );
};

const InfoRow = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl bg-[#f6f9ff] px-4 py-3">
      <span className="text-sm font-bold text-slate-500">{label}</span>
      <span className="text-right text-sm font-black text-slate-950">
        {value}
      </span>
    </div>
  );
};

const EmptyState = ({ title, desc }: { title: string; desc: string }) => {
  return (
    <div className="col-span-full rounded-[28px] border border-dashed border-slate-200 bg-white p-10 text-center">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-blue-50 text-3xl">
        📘
      </div>
      <h3 className="mt-4 text-xl font-black text-slate-950">{title}</h3>
      <p className="mx-auto mt-2 max-w-xl text-sm font-bold leading-6 text-slate-500">
        {desc}
      </p>
      <button
        onClick={() => (window.location.href = "/courses")}
        className="mt-6 rounded-2xl bg-blue-600 px-6 py-3 text-sm font-black text-white hover:bg-blue-700"
      >
        Explore Courses
      </button>
    </div>
  );
};

export default MyCourses;